export type ImportKind = "submissions" | "rankings" | "commentary";
export type ImportMode = "copy" | "move";
export type ClashChoice = "keep_target" | "take_source";
export type ImportCounts = Record<ImportKind, number>;

export type ImportSubmissionSummary = {
  id: string;
  contactName: string | null;
  languageIso: string;
  rankingTableName: string | null;
  reviewStatus: string;
  submittedAt: string;
  matterCount: number;
  clientCount: number;
  sarsOverall: number | null;
};

export type ImportRankingSummary = {
  id: string;
  kind: "firm" | "lawyer";
  tableName: string;
  subtype: string;
  contactName: string | null;
  tier: number | null;
  status: string;
};

export type ImportCommentarySummary = {
  id: string;
  contactId: string | null;
  languageIso: string;
  excerpt: string;
};

export type ImportCompareSide = {
  submissions: ImportSubmissionSummary[];
  rankings: ImportRankingSummary[];
  commentary: ImportCommentarySummary[];
};

export type ItemChoice = "incoming" | "existing" | "keep" | "remove" | "bring" | "leave";

export type ImportItemOutcome = "new" | "not_brought_across" | "kept" | "replaced" | "removed";

export type ImportItem = {
  id: string;
  kind: "submission" | "firm_ranking" | "lawyer_ranking" | "commentary";
  slot: string;
  sharedSlot: boolean;
  outcomes: Record<ClashChoice, ImportItemOutcome>;
  outcome: ImportItemOutcome;
  losesTier?: boolean;
  contactId: string | null;
  contactName: string | null;
  languageIso: string | null;
  tableName: string | null;
  landingTableName: string | null;
  landingTableIsNew: boolean;
  heldUnder?: string | null;
  decidedUnder?: string | null;
  tier: number | null;
  submittedAt: string | null;
  excerpt: string | null;
};

export type ImportPreviewOrg = {
  companyId: string | null;
  companyName: string | null;
  noFirm: boolean;
  clash: boolean;
  clashKinds: ImportKind[];
  choice: ClashChoice | null;
  sourceCounts: ImportCounts;
  importCounts: ImportCounts;
  tierLossCount?: number;
  compare: { source: ImportCompareSide; target: ImportCompareSide } | null;
  itemChoices?: Record<string, ItemChoice>;
  items?: { source: ImportItem[]; target: ImportItem[] };
};

export type SarsScaleMismatch = {
  sourceScale: number | null;
  targetScale: number | null;
  scoredSubmissions: number;
};

export type TierLoss = { rankings: number; landingTableNames: string[] };

export type ImportPreview = {
  orgs: ImportPreviewOrg[];
  summary: {
    sarsScaleMismatch?: SarsScaleMismatch | null;
    totalOrgs: number;
    clashingOrgs: number;
    counts: ImportCounts;
    keptOnTarget: Array<{ companyId: string; companyName: string | null }>;
    hiddenClerksCommentary?: number;
    tierLoss?: TierLoss;
    deletedSubmissionsRemoved?: number;
  };
};

export type ImportSelection = {
  sourceEditionBpaId: string;
  contentTypes: ImportKind[];
  excludeCompanyIds: string[];
  excludeNoFirm: boolean;
  clashResolution: Record<string, ClashChoice>;
  itemChoices?: Record<string, Record<string, ItemChoice>>;
};

/** The row key for an org: its company id, or this constant for the No firm entry. */
export const NO_FIRM_KEY = "no-firm";

export const ALL_KINDS: ImportKind[] = ["submissions", "rankings", "commentary"];

export const KIND_RANK: Record<ImportItem["kind"], number> = {
  submission: 0,
  firm_ranking: 1,
  lawyer_ranking: 2,
  commentary: 3,
};

export const KIND_OF_ITEM: Record<ImportItem["kind"], ImportKind> = {
  submission: "submissions",
  firm_ranking: "rankings",
  lawyer_ranking: "rankings",
  commentary: "commentary",
};

export function slotsInOrder<Member>(
  members: Member[],
  itemOf: (member: Member) => ImportItem,
): Member[][] {
  const slots = new Map<string, Member[]>();
  for (const member of members) {
    const slot = itemOf(member).slot;
    slots.set(slot, [...(slots.get(slot) ?? []), member]);
  }
  return [...slots.values()].sort(
    (first, second) => KIND_RANK[itemOf(first[0]).kind] - KIND_RANK[itemOf(second[0]).kind],
  );
}

export type ImportPageState = {
  sourceEditionBpaId: string | null;
  kinds: ImportKind[];
  mode: ImportMode;
  unticked: string[];
  choices: Record<string, ClashChoice>;
  itemChoices?: Record<string, Record<string, ItemChoice>>;
  clicked?: Record<string, string[]>;
};

export type SourceCandidate = { id: string; editionId: string; status: string };

export function sourceCandidates<T extends SourceCandidate>(
  editionBpas: T[],
  target: { id: string; editionId: string },
  sourceEditionId: string = target.editionId,
): T[] {
  return editionBpas.filter(
    bpa => bpa.editionId === sourceEditionId && bpa.id !== target.id && bpa.status !== "archived",
  );
}

export function buildImportSelection(state: ImportPageState): ImportSelection | null {
  if (!state.sourceEditionBpaId) return null;
  const itemChoices = tickedItemChoices(state);
  return {
    sourceEditionBpaId: state.sourceEditionBpaId,
    contentTypes: state.kinds,
    excludeCompanyIds: state.unticked.filter(key => key !== NO_FIRM_KEY),
    excludeNoFirm: state.unticked.includes(NO_FIRM_KEY),
    clashResolution: state.choices,
    ...(Object.keys(itemChoices).length > 0 ? { itemChoices } : {}),
  };
}

function tickedItemChoices(state: ImportPageState): Record<string, Record<string, ItemChoice>> {
  return Object.fromEntries(
    Object.entries(state.itemChoices ?? {}).filter(
      ([key, choices]) => !state.unticked.includes(key) && Object.keys(choices).length > 0,
    ),
  );
}

export function changeSource(state: ImportPageState, sourceEditionBpaId: string): ImportPageState {
  return { ...state, sourceEditionBpaId, unticked: [], choices: {} };
}

export type ImportSummary = { organisations: number; counts: ImportCounts };

export function firmKey(org: ImportPreviewOrg): string {
  return org.companyId ?? NO_FIRM_KEY;
}

function emptyCounts(): ImportCounts {
  return { submissions: 0, rankings: 0, commentary: 0 };
}

function countsContributedBy(org: ImportPreviewOrg, state: ImportPageState): ImportCounts {
  const key = firmKey(org);
  if (state.unticked.includes(key)) return emptyCounts();
  if (org.clash && (state.choices[key] ?? "keep_target") !== "take_source") return emptyCounts();
  const counts = emptyCounts();
  for (const kind of state.kinds) counts[kind] = org.sourceCounts[kind];
  return counts;
}

function bringsContentAcross(counts: ImportCounts): boolean {
  return counts.submissions + counts.rankings + counts.commentary > 0;
}

export function summariseImport(orgs: ImportPreviewOrg[], state: ImportPageState): ImportSummary {
  const total = emptyCounts();
  let organisations = 0;
  for (const org of orgs) {
    const counts = countsContributedBy(org, state);
    total.submissions += counts.submissions;
    total.rankings += counts.rankings;
    total.commentary += counts.commentary;
    if (bringsContentAcross(counts)) organisations += 1;
  }
  return { organisations, counts: total };
}

/**
 * The company ids whose content the import brings across: the organisations `summariseImport`
 * counts, minus No firm, which has no row on the practice area page.
 */
export function importedCompanyIds(orgs: ImportPreviewOrg[], state: ImportPageState): string[] {
  const ids: string[] = [];
  for (const org of orgs) {
    if (org.companyId === null) continue;
    if (bringsContentAcross(countsContributedBy(org, state))) ids.push(org.companyId);
  }
  return ids;
}

export function plural(count: number, singular: string): string {
  return `${count} ${count === 1 ? singular : `${singular}s`}`;
}

export function describeImportSummary(
  mode: ImportMode,
  summary: ImportSummary,
  targetName: string,
  sourceLabel?: string,
): { sentence: string; counts: string } {
  const verb = mode === "move" ? "Move" : "Copy";
  const { counts } = summary;
  const from = sourceLabel ? ` from ${sourceLabel}` : "";
  return {
    sentence: `${verb} ${plural(summary.organisations, "organisation")}${from} into ${targetName}`,
    counts: `${plural(counts.submissions, "submission")} · ${plural(counts.rankings, "ranking")} · ${counts.commentary} commentary`,
  };
}

export type ReviewCounts = {
  organisations: number;
  counts: ImportCounts;
  keptOnTarget: string[];
};

export function reviewCounts(preview: ImportPreview): ReviewCounts {
  const organisations = preview.orgs.filter(
    org =>
      org.importCounts.submissions + org.importCounts.rankings + org.importCounts.commentary > 0,
  ).length;
  return {
    organisations,
    counts: preview.summary.counts,
    keptOnTarget: preview.summary.keptOnTarget.map(org => org.companyName ?? org.companyId),
  };
}

export function canImportContent(
  flagOn: boolean,
  hasPermission: (permission: string) => boolean,
): boolean {
  return flagOn && hasPermission("staff.research.edit");
}

export function canReplaceTargetContent(hasPermission: (permission: string) => boolean): boolean {
  return hasPermission("staff.research.delete");
}

export function canMove(hasPermission: (permission: string) => boolean): boolean {
  return hasPermission("staff.research.delete");
}

function joinNames(names: string[]): string {
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

export function deleteRefusalMessage(
  names: string[],
  copiedOnly = false,
): { title: string; body: string[] } {
  const title = "Archive this practice area instead";
  const archive = "Archive it to hide it from the edition.";
  const where = copiedOnly ? "Its content was copied into" : "Its content was moved into";
  if (names.length === 0) return { title, body: [archive] };
  if (names.length === 1) {
    return { title, body: [`${where} ${names[0]}. ${archive}`] };
  }
  return { title, body: [`${where}: ${joinNames(names)}.`, archive] };
}

export type ImportDeleteRefusal = { names: string[]; copiedOnly: boolean };

/** The practice areas a refused delete names, or null for any other failure. */
export function readImportDeleteRefusal(
  error: unknown,
  currentEditionId?: string,
): ImportDeleteRefusal | null {
  const { status, detailsRaw } = error as {
    status?: number;
    detailsRaw?: {
      reason?: string;
      importedInto?: string[];
      importedIntoBpas?: NamedPracticeArea[];
      copiedOnly?: boolean;
    };
  };
  if (status !== 409) return null;
  if (detailsRaw?.reason !== "part_of_import") return null;
  const names =
    detailsRaw.importedIntoBpas && currentEditionId !== undefined
      ? detailsRaw.importedIntoBpas.map(area => practiceAreaLabel(area, currentEditionId))
      : (detailsRaw.importedInto ?? []);
  return { names, copiedOnly: detailsRaw.copiedOnly === true };
}

export function defaultJurisdiction(
  sourceEditionId: string,
  target: { editionId: string; rankingEntityId: string | null },
): string {
  if (sourceEditionId !== target.editionId) return "";
  return target.rankingEntityId ?? "";
}

export type NamedPracticeArea = {
  name: string;
  editionId: string;
  editionName: string | null;
  publicationName: string | null;
};

function editionLabel(publicationName: string | null, editionName: string | null): string {
  if (publicationName && editionName?.toLowerCase().includes(publicationName.toLowerCase())) {
    return editionName;
  }
  return [publicationName, editionName].filter(Boolean).join(", ");
}

export function practiceAreaLabel(area: NamedPracticeArea, currentEditionId: string): string {
  if (area.editionId === currentEditionId) return area.name;
  const where = editionLabel(area.publicationName, area.editionName);
  return where ? `${area.name} (${where})` : area.name;
}

export type ImportHistoryNames = {
  sourceEditionBpaId: string;
  sourceBpaId: string;
  sourceBpaName: string;
  sourceEditionId: string;
  sourceEditionName: string | null;
  sourcePublicationName: string | null;
  targetEditionBpaId: string;
  targetBpaId: string;
  targetBpaName: string;
  targetEditionId: string;
  targetEditionName: string | null;
  targetPublicationName: string | null;
  mode?: ImportMode;
  purpose?: "import" | "split" | "merge";
};

function historyVerb(entry: ImportHistoryNames): string {
  if (entry.purpose === "split") return "Split";
  if (entry.purpose === "merge") return "Merged";
  if (entry.mode === "copy") return "Copied";
  if (entry.mode === "move") return "Moved";
  return "Imported";
}

export function importHistoryLink(
  entry: ImportHistoryNames,
  editionBpaId: string,
  currentEditionId: string,
): { direction: string; label: string; editionId: string; bpaId: string } {
  if (entry.targetEditionBpaId === editionBpaId) {
    const area = {
      name: entry.sourceBpaName,
      editionId: entry.sourceEditionId,
      editionName: entry.sourceEditionName,
      publicationName: entry.sourcePublicationName,
    };
    return {
      direction: `${historyVerb(entry)} from`,
      label: practiceAreaLabel(area, currentEditionId),
      editionId: entry.sourceEditionId,
      bpaId: entry.sourceBpaId,
    };
  }
  const area = {
    name: entry.targetBpaName,
    editionId: entry.targetEditionId,
    editionName: entry.targetEditionName,
    publicationName: entry.targetPublicationName,
  };
  return {
    direction: `${historyVerb(entry)} into`,
    label: practiceAreaLabel(area, currentEditionId),
    editionId: entry.targetEditionId,
    bpaId: entry.targetBpaId,
  };
}
